import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db } from "../Config/firebase";
import {
  aplicarTenantAtual,
  consultaTenantAtual,
  getUsuarioAtualComTenant,
} from "../Utils/tenant";
import { gerarSequencial } from "../Utils/sequencial";
import RelatorioGestaoMudancasPrint from "./RelatorioGestaoMudancasPrint";

const COLLECTION = "tblGestaoMudancas";

function dataHoje() {
  const agora = new Date();
  const local = new Date(agora.getTime() - agora.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function Secao({ numero, titulo, children }) {
  return (
    <Paper sx={{ border: "1px solid #d7e0ea", mb: 2, overflow: "hidden" }}>
      <Typography
        fontWeight={700}
        sx={{ backgroundColor: "#d6e6f6", px: 2, py: 1 }}
      >
        {numero}. {titulo}
      </Typography>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function identificarUsuario(usuario) {
  if (!usuario) return "";
  const nome = String(usuario.nome || "").trim();
  const email = String(usuario.email || "").trim();
  return nome && email ? `${nome} - ${email}` : nome || email;
}

function formatarDataBR(data) {
  const [ano, mes, dia] = String(data || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data || "-";
}

function obterEmailAprovador(registro) {
  const emailSalvo = String(registro?.aprovacaoUsuario?.email || "").trim();
  if (emailSalvo) return emailSalvo;
  return String(registro?.aprovacaoNome || "").match(
    /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/
  )?.[0] || "";
}

export default function DetalhesGestaoMudancas() {
  const { registroId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [registro, setRegistro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [modoRelatorio, setModoRelatorio] = useState(false);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [aprovacaoPersistida, setAprovacaoPersistida] = useState(false);

  useEffect(() => {
    setModoRelatorio(false);
    if (registroId === "novo") {
      carregarNovoRegistro();
    } else {
      carregarRegistro();
      carregarUsuarioLogado();
    }
  }, [registroId]);

  useEffect(() => {
    if (registro && new URLSearchParams(location.search).get("print") === "1") {
      setModoRelatorio(true);
    }
  }, [location.search, registro]);

  async function carregarRegistro() {
    const snap = await getDoc(doc(db, COLLECTION, registroId));
    if (!snap.exists()) {
      alert("Registro de gestão de mudanças não encontrado.");
      history.push("/app/gestao-mudancas");
      return;
    }

    const dados = { id: snap.id, ...snap.data() };
    setAprovacaoPersistida(dados.aprovadoDirecao === true);
    setRegistro({
      ...dados,
      statusImplementacao: dados.dataConclusao
        ? "Concluída"
        : dados.statusImplementacao || "Em andamento",
      responsavelVerificacao:
        dados.aprovadoDirecao === true
          ? dados.responsavelVerificacao || ""
          : "",
      motivos: {
        melhoriaProcesso: false,
        mudancaTecnologica: false,
        decisaoDirecao: false,
        exigenciaNormativa: false,
        resultadoAuditoria: false,
        outro: false,
        outroDescricao: "",
        ...(dados.motivos || {}),
      },
      recursos: {
        treinamento: false,
        equipamento: false,
        infraestrutura: false,
        sistema: false,
        atualizacaoDocumental: false,
        ...(dados.recursos || {}),
      },
    });
  }

  async function carregarNovoRegistro() {
    const usuario = await getUsuarioAtualComTenant();
    const solicitante = String(usuario.nome || usuario.email || "").trim();

    if (!solicitante) {
      alert("Não foi possível identificar o usuário logado.");
      history.push("/app/gestao-mudancas");
      return;
    }

    setUsuarioLogado(usuario);
    setAprovacaoPersistida(false);
    setRegistro({
      id: null,
      codigo: "",
      numeroRegistro: null,
      solicitante,
      solicitanteUsuario: {
        uid: usuario.uid || "",
        nome: usuario.nome || "",
        email: usuario.email || "",
      },
      dataSolicitacao: dataHoje(),
      areaImpactada: "",
      descricaoMudanca: "",
      motivos: {
        melhoriaProcesso: false,
        mudancaTecnologica: false,
        decisaoDirecao: false,
        exigenciaNormativa: false,
        resultadoAuditoria: false,
        outro: false,
        outroDescricao: "",
      },
      recursos: {
        treinamento: false,
        equipamento: false,
        infraestrutura: false,
        sistema: false,
        atualizacaoDocumental: false,
      },
      propositoConsequencias: "",
      impactosIntegridade: "",
      responsavelImplementacao: "",
      responsaveisAcompanhamento: "",
      aprovacaoNome: "",
      aprovacaoData: "",
      dataInicio: "",
      dataConclusao: "",
      statusImplementacao: "Em andamento",
      eficaciaResultado: "",
      descricaoVerificacao: "",
      responsavelVerificacao: "",
      dataVerificacao: "",
      cargoVerificacao: "",
      aprovadoDirecao: false,
      aprovacaoUsuario: null,
      aprovadoEm: null,
    });
  }

  async function carregarUsuarioLogado() {
    setUsuarioLogado(await getUsuarioAtualComTenant());
  }

  function atualizarCampo(campo, valor) {
    setMensagem("");
    setRegistro((atual) => ({ ...atual, [campo]: valor }));
  }

  function atualizarMarcacao(grupo, campo, valor) {
    setMensagem("");
    setRegistro((atual) => ({
      ...atual,
      [grupo]: {
        ...(atual[grupo] || {}),
        [campo]: valor,
      },
    }));
  }

  function atualizarAprovacaoData(data) {
    setMensagem("");
    setRegistro((atual) => ({
      ...atual,
      aprovacaoData: data,
    }));
  }

  function alternarAprovacaoFinal(aprovado) {
    setMensagem("");

    if (aprovado && !registro.aprovacaoData) {
      alert("Informe a data da aprovação antes de confirmar.");
      return;
    }

    setRegistro((atual) => ({
      ...atual,
      aprovadoDirecao: aprovado,
      aprovacaoNome: aprovado ? identificarUsuario(usuarioLogado) : "",
      aprovacaoUsuario: aprovado
        ? {
            uid: usuarioLogado?.uid || "",
            nome: usuarioLogado?.nome || "",
            email: usuarioLogado?.email || "",
          }
        : null,
      aprovadoEm: aprovado ? new Date() : null,
      responsavelVerificacao: aprovado
        ? String(usuarioLogado?.nome || usuarioLogado?.email || "").trim()
        : "",
      aguardandoAprovacao: false,
    }));
  }

  function atualizarVerificacao(campo, valor) {
    setMensagem("");
    setRegistro((atual) => ({
      ...atual,
      [campo]: valor,
    }));
  }

  async function solicitarAlteracao() {
    if (!usuarioLogado) return;

    const solicitacaoAlteracao = {
      status: "pendente",
      solicitadoPor: {
        uid: usuarioLogado.uid || "",
        nome: usuarioLogado.nome || "",
        email: usuarioLogado.email || "",
      },
      solicitadoEm: new Date(),
    };

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, registro.id), {
        solicitacaoAlteracao,
        atualizadoEm: new Date(),
      });
      setRegistro((atual) => ({ ...atual, solicitacaoAlteracao }));
      setMensagem("Solicitação de alteração enviada ao usuário que aprovou.");
    } finally {
      setSalvando(false);
    }
  }

  async function decidirSolicitacaoAlteracao(autorizar) {
    const uidAprovador = String(registro.aprovacaoUsuario?.uid || "");
    const emailAprovador = obterEmailAprovador(registro);
    const usuarioEhAprovador =
      (uidAprovador && uidAprovador === usuarioLogado?.uid) ||
      (!uidAprovador &&
        emailAprovador &&
        emailAprovador === usuarioLogado?.email);

    if (!usuarioEhAprovador) {
      alert("Somente o usuário que aprovou pode autorizar esta alteração.");
      return;
    }

    const decididoEm = new Date();
    const solicitacaoAlteracao = {
      ...(registro.solicitacaoAlteracao || {}),
      status: autorizar ? "autorizada" : "negada",
      decididoPor: {
        uid: usuarioLogado.uid || "",
        nome: usuarioLogado.nome || "",
        email: usuarioLogado.email || "",
      },
      decididoEm,
    };
    const alteracoes = autorizar
      ? {
          aprovadoDirecao: false,
          aprovacaoNome: "",
          aprovacaoData: "",
          aprovacaoUsuario: null,
          aprovadoEm: null,
          responsavelVerificacao: "",
          historicoAprovacoes: [
            ...(registro.historicoAprovacoes || []),
            {
              nome: registro.aprovacaoNome || "",
              data: registro.aprovacaoData || "",
              usuario: registro.aprovacaoUsuario || null,
              aprovadoEm: registro.aprovadoEm || null,
              alteracaoAutorizadaEm: decididoEm,
            },
          ],
        }
      : {};

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, registro.id), {
        ...alteracoes,
        solicitacaoAlteracao,
        atualizadoEm: decididoEm,
      });
      setRegistro((atual) => ({
        ...atual,
        ...alteracoes,
        solicitacaoAlteracao,
        atualizadoEm: decididoEm,
      }));
      if (autorizar) setAprovacaoPersistida(false);
      setMensagem(
        autorizar
          ? "Alteração autorizada. O registro foi liberado para edição e deverá ser aprovado novamente."
          : "Solicitação de alteração não autorizada."
      );
    } finally {
      setSalvando(false);
    }
  }

  async function salvarRegistro() {
    if (aprovacaoPersistida) {
      alert(
        "Este registro está aprovado e não pode ser editado sem autorização."
      );
      return;
    }

    if (!registro.solicitante?.trim()) {
      alert("Informe o solicitante.");
      return;
    }
    if (!registro.dataSolicitacao) {
      alert("Informe a data da solicitação.");
      return;
    }
    if (!registro.areaImpactada?.trim()) {
      alert("Informe a área impactada.");
      return;
    }
    if (!registro.descricaoMudanca?.trim()) {
      alert("Informe a descrição da mudança.");
      return;
    }
    if (registro.aprovadoDirecao === true && !registro.aprovacaoData) {
      alert("Informe a data da aprovação.");
      return;
    }

    setSalvando(true);
    try {
      const { id, ...dadosOriginais } = registro;
      const dados = {
        ...dadosOriginais,
        aprovacaoNome:
          dadosOriginais.aprovadoDirecao === true
            ? dadosOriginais.aprovacaoNome || identificarUsuario(usuarioLogado)
            : "",
      };
      const atualizadoEm = new Date();

      if (!id) {
        const snap = await getDocs(
          consultaTenantAtual(collection(db, COLLECTION))
        );
        const maiorNumero = snap.docs.reduce((maior, item) => {
          const numero = Number(item.data().numeroRegistro || 0);
          return Number.isFinite(numero) && numero > maior ? numero : maior;
        }, 0);
        const sequencial = await gerarSequencial({
          chave: "gestao_mudancas",
          valorMinimo: maiorNumero,
        });
        const numeroRegistro = sequencial.numero;
        const ano =
          String(dados.dataSolicitacao).slice(0, 4) ||
          String(new Date().getFullYear());
        const codigo = `GM-${String(numeroRegistro).padStart(3, "0")}/${ano}`;
        const dadosCriacao = aplicarTenantAtual({
          ...dados,
          numeroRegistro,
          codigo,
          criadoEm: atualizadoEm,
          atualizadoEm,
        });
        const ref = await addDoc(collection(db, COLLECTION), dadosCriacao);
        setRegistro({ id: ref.id, ...dadosCriacao });
        history.replace(`/app/gestao-mudancas/${ref.id}`);
      } else {
        await updateDoc(doc(db, COLLECTION, id), {
          ...dados,
          atualizadoEm,
        });
        setRegistro((atual) => ({ ...atual, ...dados, atualizadoEm }));
      }

      setAprovacaoPersistida(dados.aprovadoDirecao === true);
      setMensagem("Registro salvo com sucesso.");
    } finally {
      setSalvando(false);
    }
  }

  if (!registro) return null;

  if (modoRelatorio) {
    return (
      <RelatorioGestaoMudancasPrint
        registro={registro}
        onVoltar={() => setModoRelatorio(false)}
      />
    );
  }

  const edicaoBloqueada = aprovacaoPersistida;
  const solicitacaoAlteracao = registro.solicitacaoAlteracao || {};
  const uidAprovador = String(registro.aprovacaoUsuario?.uid || "");
  const emailAprovador = obterEmailAprovador(registro);
  const usuarioEhAprovador =
    (uidAprovador && uidAprovador === usuarioLogado?.uid) ||
    (!uidAprovador &&
      emailAprovador &&
      emailAprovador === usuarioLogado?.email);

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "space-between",
            mb: 2,
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Registro de Gestão de Mudanças
            </Typography>
            <Typography color="text.secondary">
              {registro.codigo || "Novo registro"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/app/gestao-mudancas")}
            >
              Voltar
            </Button>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setModoRelatorio(true)}
              disabled={!registro.id}
            >
              Relatório
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={salvarRegistro}
              disabled={salvando || edicaoBloqueada}
            >
              {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </Stack>
        </Paper>

        {mensagem && (
          <Typography color="success.main" fontWeight={700} mb={2}>
            {mensagem}
          </Typography>
        )}

        <Box
          component="fieldset"
          disabled={edicaoBloqueada}
          sx={{ border: 0, m: 0, minWidth: 0, p: 0 }}
        >
        <Secao numero="1" titulo="Identificação da Mudança">
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Solicitante"
                value={registro.solicitante || ""}
                helperText="Preenchido automaticamente com o usuário que criou o registro."
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <TextField
                label="Data da solicitação"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={registro.dataSolicitacao || ""}
                onChange={(event) =>
                  atualizarCampo("dataSolicitacao", event.target.value)
                }
                fullWidth
              />
            </Stack>
            <TextField
              label="Área impactada"
              value={registro.areaImpactada || ""}
              onChange={(event) => atualizarCampo("areaImpactada", event.target.value)}
              fullWidth
            />
            <TextField
              label="Descrição da mudança"
              value={registro.descricaoMudanca || ""}
              onChange={(event) =>
                atualizarCampo("descricaoMudanca", event.target.value)
              }
              multiline
              minRows={4}
              fullWidth
            />
          </Stack>
        </Secao>

        <Secao numero="2" titulo="Motivo da Mudança">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 0.5,
            }}
          >
            {[
              ["melhoriaProcesso", "Melhoria de processo"],
              ["exigenciaNormativa", "Exigência normativa"],
              ["mudancaTecnologica", "Mudança tecnológica"],
              ["resultadoAuditoria", "Resultado de auditoria"],
              ["decisaoDirecao", "Decisão da direção"],
              ["outro", "Outro"],
            ].map(([campo, rotulo]) => (
              <FormControlLabel
                key={campo}
                control={
                  <Checkbox
                    checked={Boolean(registro.motivos?.[campo])}
                    onChange={(event) =>
                      atualizarMarcacao("motivos", campo, event.target.checked)
                    }
                  />
                }
                label={rotulo}
              />
            ))}
          </Box>
          {registro.motivos?.outro && (
            <TextField
              label="Outro motivo"
              value={registro.motivos.outroDescricao || ""}
              onChange={(event) =>
                atualizarMarcacao("motivos", "outroDescricao", event.target.value)
              }
              fullWidth
              sx={{ mt: 1 }}
            />
          )}
        </Secao>

        <Secao numero="3" titulo="Avaliação da Mudança">
          <Stack spacing={2}>
            <TextField
              label="Propósito da mudança e possíveis consequências"
              value={registro.propositoConsequencias || ""}
              onChange={(event) =>
                atualizarCampo("propositoConsequencias", event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Impactos na integridade do Sistema de Gestão da Qualidade"
              value={registro.impactosIntegridade || ""}
              onChange={(event) =>
                atualizarCampo("impactosIntegridade", event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />
            <Typography fontWeight={700}>Recursos necessários para implementação</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              {[
                ["treinamento", "Treinamento"],
                ["sistema", "Sistema"],
                ["equipamento", "Equipamento"],
                ["atualizacaoDocumental", "Atualização documental"],
                ["infraestrutura", "Infraestrutura"],
              ].map(([campo, rotulo]) => (
                <FormControlLabel
                  key={campo}
                  control={
                    <Checkbox
                      checked={Boolean(registro.recursos?.[campo])}
                      onChange={(event) =>
                        atualizarMarcacao("recursos", campo, event.target.checked)
                      }
                    />
                  }
                  label={rotulo}
                />
              ))}
            </Box>
          </Stack>
        </Secao>

        <Secao numero="4" titulo="Responsabilidades">
          <Stack spacing={2}>
            <TextField
              label="Responsável pela implementação"
              value={registro.responsavelImplementacao || ""}
              onChange={(event) =>
                atualizarCampo("responsavelImplementacao", event.target.value)
              }
              fullWidth
            />
            <TextField
              label="Responsável pelo acompanhamento"
              value={registro.responsaveisAcompanhamento || ""}
              onChange={(event) =>
                atualizarCampo("responsaveisAcompanhamento", event.target.value)
              }
              fullWidth
            />
          </Stack>
        </Secao>

        <Secao numero="5" titulo="Aprovação da Direção">
          <Typography color="text.secondary">
            A aprovação da direção será registrada na validação final, após a
            leitura completa deste documento.
          </Typography>
          {registro.aprovadoDirecao === true && (
            <Typography mt={1} color="success.main" fontWeight={700}>
              Aprovado por {registro.aprovacaoNome} em{" "}
              {formatarDataBR(registro.aprovacaoData)}.
            </Typography>
          )}
        </Secao>

        <Secao numero="6" titulo="Implementação">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Data de início"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={registro.dataInicio || ""}
              onChange={(event) => atualizarCampo("dataInicio", event.target.value)}
              fullWidth
            />
            <TextField
              label="Data de conclusão"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={registro.dataConclusao || ""}
              onChange={(event) => {
                const dataConclusao = event.target.value;
                setMensagem("");
                setRegistro((atual) => ({
                  ...atual,
                  dataConclusao,
                  statusImplementacao: dataConclusao
                    ? "Concluída"
                    : atual.statusImplementacao,
                }));
              }}
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={registro.statusImplementacao || "Em andamento"}
              onChange={(event) =>
                atualizarCampo("statusImplementacao", event.target.value)
              }
              disabled={Boolean(registro.dataConclusao)}
              helperText={
                registro.dataConclusao
                  ? "O status fica bloqueado enquanto houver uma data de conclusão."
                  : ""
              }
              fullWidth
            >
              <MenuItem value="Planejada">Planejada</MenuItem>
              <MenuItem value="Em andamento">Em andamento</MenuItem>
              <MenuItem value="Concluída">Concluída</MenuItem>
              <MenuItem value="Cancelada">Cancelada</MenuItem>
            </TextField>
          </Stack>
        </Secao>

        <Secao numero="7" titulo="Verificação de Eficácia">
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>A mudança atingiu o resultado esperado?</FormLabel>
              <RadioGroup
                row
                value={registro.eficaciaResultado || ""}
                onChange={(event) =>
                  atualizarVerificacao("eficaciaResultado", event.target.value)
                }
              >
                <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                <FormControlLabel value="nao" control={<Radio />} label="Não" />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Descrição da verificação"
              value={registro.descricaoVerificacao || ""}
              onChange={(event) =>
                atualizarVerificacao("descricaoVerificacao", event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Responsável pela verificação"
                value={registro.responsavelVerificacao || ""}
                helperText="Preenchido automaticamente pelo usuário que realizar a validação final."
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <TextField
                label="Cargo do responsável"
                value={registro.cargoVerificacao || ""}
                onChange={(event) =>
                  atualizarVerificacao("cargoVerificacao", event.target.value)
                }
                placeholder="Ex.: Diretor"
                fullWidth
              />
              <TextField
                label="Data da verificação"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={registro.dataVerificacao || ""}
                onChange={(event) =>
                  atualizarVerificacao("dataVerificacao", event.target.value)
                }
                fullWidth
              />
            </Stack>
          </Stack>
        </Secao>
        </Box>

        {edicaoBloqueada ? (
          <Paper
            variant="outlined"
            sx={{ borderColor: "success.main", p: 2, mt: 2 }}
          >
            <Typography variant="h6" color="success.main" fontWeight={700}>
              Registro aprovado e bloqueado para edição
            </Typography>
            <Typography mt={1}>
              Aprovado por {registro.aprovacaoNome} em{" "}
              {formatarDataBR(registro.aprovacaoData)}.
            </Typography>

            {solicitacaoAlteracao.status === "pendente" ? (
              <Box mt={2}>
                <Typography color="warning.main" fontWeight={700}>
                  Alteração solicitada por{" "}
                  {identificarUsuario(solicitacaoAlteracao.solicitadoPor)}.
                </Typography>
                {usuarioEhAprovador ? (
                  <Stack direction="row" spacing={1} mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => decidirSolicitacaoAlteracao(true)}
                      disabled={salvando}
                    >
                      Autorizar alteração
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => decidirSolicitacaoAlteracao(false)}
                      disabled={salvando}
                    >
                      Não autorizar
                    </Button>
                  </Stack>
                ) : (
                  <Typography color="text.secondary" mt={1}>
                    Aguardando autorização do usuário que aprovou o registro.
                  </Typography>
                )}
              </Box>
            ) : (
              <Button
                variant="outlined"
                onClick={solicitarAlteracao}
                disabled={salvando}
                sx={{ mt: 2 }}
              >
                Solicitar autorização para alterar
              </Button>
            )}
          </Paper>
        ) : (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Validação final da direção
          </Typography>
          <Typography color="text.secondary" mb={2}>
            Confirme a aprovação somente depois de ler todas as informações do
            registro.
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Usuário responsável pela aprovação"
                value={
                  registro.aprovacaoNome || identificarUsuario(usuarioLogado)
                }
                helperText="Nome e e-mail obtidos automaticamente do usuário autenticado."
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <TextField
                label="Data da aprovação"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={registro.aprovacaoData || ""}
                onChange={(event) =>
                  atualizarAprovacaoData(event.target.value)
                }
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={registro.aprovadoDirecao === true}
                  onChange={(event) =>
                    alternarAprovacaoFinal(event.target.checked)
                  }
                />
              }
              label="Li o registro completo e aprovo esta gestão de mudança."
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={salvarRegistro}
              disabled={salvando}
              sx={{ alignSelf: "flex-end" }}
            >
              {salvando ? "Salvando..." : "Salvar aprovação"}
            </Button>
          </Stack>
        </Paper>
        )}
      </Box>
    </>
  );
}
